"use client";

import { useState, useEffect, useRef, type ReactNode, type KeyboardEvent } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Search, X, Loader2, FileQuestion } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import LaTeXRenderer from "@/components/LaTeXRenderer";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  _id: string;
  question: {
    _id: string;
    questionNumber: number;
    text: string;
    topicNames?: string[];
    examPaper: { subject: string; year: number; paperCode: string };
  };
}

type Status = "idle" | "loading" | "done" | "error";

const EXAMPLE_QUERIES = ["integration", "matrices", "probability", "logarithms", "vectors"];

const getPaperNumber = (paperCode: string) => {
  const match = paperCode.match(/(\d{2})$/) || paperCode.match(/(\d)$/);
  return match ? parseInt(match[1], 10) : paperCode;
};

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-muted px-1 font-sans text-[10px] text-muted-foreground">
      {children}
    </kbd>
  );
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [status, setStatus] = useState<Status>("idle");
  const [selected, setSelected] = useState(0);
  const debouncedQuery = useDebounce(query, 250);
  const router = useRouter();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Reset to a clean palette each time it opens.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setStatus("idle");
      setSelected(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q) {
      setResults([]);
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    fetch(`/api/search?query=${encodeURIComponent(q)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        return Array.isArray(data) ? (data as SearchResult[]) : [];
      })
      .then((data) => {
        if (cancelled) return;
        setResults(data);
        setStatus("done");
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    setSelected(0);
  }, [results]);

  useEffect(() => {
    itemRefs.current[selected]?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const openResult = (result: SearchResult) => {
    router.push(
      `/dashboard/search/${encodeURIComponent(debouncedQuery)}?questionId=${result.question._id}`,
    );
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const result = results[selected];
      if (result) openResult(result);
    }
  };

  const hasResults = results.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="w-full max-w-2xl gap-0 overflow-hidden p-0"
      >
        <DialogTitle className="sr-only">Search past paper questions</DialogTitle>

        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search past paper questions, topics, or exam papers…"
            className="h-14 w-full bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          {status === "loading" ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="shrink-0 rounded p-1 text-muted-foreground transition-transform hover:text-foreground active:scale-95"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* Body — sized to content: results scroll to a max, states stay compact */}
        {hasResults ? (
          <div className="max-h-[24rem] overflow-y-auto overscroll-contain p-2">
            {results.map((result, i) => (
              <div
                key={result._id}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                onClick={() => openResult(result)}
                onMouseMove={() => setSelected(i)}
                className={cn(
                  "cursor-pointer rounded-lg px-3 py-3 transition-colors",
                  i === selected
                    ? "bg-primary/10 ring-1 ring-inset ring-primary/30"
                    : "hover:bg-muted/50",
                )}
              >
                <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground/80">
                    Question {result.question.questionNumber}
                  </span>
                  <span className="truncate">
                    {result.question.examPaper.subject} · {result.question.examPaper.year} · Paper{" "}
                    {getPaperNumber(result.question.examPaper.paperCode)}
                  </span>
                </div>
                {result.question.topicNames && result.question.topicNames.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {[...new Set(result.question.topicNames)].map((topic) => (
                      <Badge key={topic} variant="secondary" className="text-[10px]">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                )}
                <div
                  className="max-h-16 overflow-hidden text-sm text-foreground/90"
                  style={{
                    maskImage: "linear-gradient(to bottom, black 55%, transparent)",
                    WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent)",
                  }}
                >
                  <LaTeXRenderer content={result.question.text} />
                </div>
              </div>
            ))}
          </div>
        ) : status === "loading" ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : status === "error" ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <p className="text-sm font-medium text-foreground">Search is unavailable right now</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Something went wrong running that search. Please try again in a moment.
            </p>
          </div>
        ) : debouncedQuery.trim() ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <FileQuestion className="mb-3 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">
              No results for &ldquo;{debouncedQuery}&rdquo;
            </p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Try a different topic or keyword.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Search className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-foreground">Search past paper questions</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Find questions by topic, keyword, or concept across every past paper.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {EXAMPLE_QUERIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuery(q)}
                  className="rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground transition-transform hover:bg-muted hover:text-foreground active:scale-[0.97]"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer hints */}
        <div className="hidden h-9 items-center gap-4 border-t border-border px-4 text-[11px] text-muted-foreground sm:flex">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <Kbd>↵</Kbd>
            open
          </span>
          <span className="flex items-center gap-1">
            <Kbd>esc</Kbd>
            close
          </span>
          {hasResults && (
            <span className="ml-auto tabular-nums">
              {results.length} result{results.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
